import React, { memo, useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import {
  Stack,
  Chip,
  Card,
  Typography,
  LoadingButton,
  Autocomplete,
  TextField,
  CircularProgress,
  Icon,
  useMediaQuery
} from 'investira.react.components';
import { validators, formats } from 'investira.sdk';
import { DatePicker, PessoaImage } from '../../components/molecules';
import { CorrelacaoMatriz } from '../../components/organisms';
import { BaseTemplate } from '../../components/templates';
import { VALIDATIONS } from '../../const';
import services from '../../services';
import HeatMap from './_components/HeatMap';
import VariacaoChart from './_components/VariacaoChart';
import CorrelacaoList from './_components/CorrelacaoList';
import withResponseHandling from '../../hoc/withResponseHandling';
import utils from '../../utils';
import { displays } from 'investira.react.lib';
import { ATIVO_MERCADO } from '@investirapri/financa.js/lib/enums';

const CorrelacaoPares = memo(props => {
  const [pesquisaInput, setPesquisaInput] = useState('');
  const [pesquisaOptions, setPesquisaOptions] = useState([]);
  const [pesquisaLoading, setPesquisaLoading] = useState(false);
  const [data, setData] = useState({});

  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }

  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, '');
    }

    return pStr;
  }

  function removeDoubleSpaces(pStr) {
    return pStr.replace(/\s{2,}/g, ' ').trim();
  }

  function removeEmptyItems(pArr) {
    return pArr.filter(item => item !== '');
  }

  function formatSearch(pValue) {
    let xValue = removeDoubleSpaces(pValue);
    xValue = cleanSpecialChar(pValue);

    let xStrings = xValue.split(' ');
    xStrings = removeEmptyItems(xStrings);
    xStrings = xStrings.map(xString => {
      return `+${xString}*`.trim();
    });

    const xSearchFormated = xStrings.join('');

    return xSearchFormated;
  }

  // Função para buscar opções do autocomplete
  useEffect(() => {
    let active = true;
    if (!pesquisaInput || pesquisaInput.length < 2) {
      setPesquisaOptions([]);
      setPesquisaLoading(false);
      return undefined;
    }

    setPesquisaLoading(true);

    const xPesquisa = formatSearch(pesquisaInput);

    services.pesquisas.correlacao(
      { pesquisa: xPesquisa },
      rRes => {
        if (active) {
          setPesquisaOptions(rRes.data || []);
        }
      },
      rErr => {
        if (active) {
          setPesquisaOptions([]);
        }
      },
      () => {
        if (active) {
          setPesquisaLoading(false);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [pesquisaInput]);

  /**
   * Separa os elementos do formik em dois grupos: indicador_id e ativo_id,
   * baseado no atributo pesquisa_origem de cada elemento.
   * @param {Array} elementos - Array de elementos do formik
   * @returns {Object} { indicador_id: [], ativo_id: [] }
   */
  function separarElementosPorOrigem(pElementos) {
    if (!Array.isArray(pElementos)) return [];

    return pElementos.reduce((acc, el) => {
      if (el && el.pesquisa_origem === 'indicador' && el.indicador_id) {
        acc.push({ indicador_id: el.indicador_id });
      } else if (el && el.pesquisa_origem === 'ativo' && el.ativo_id) {
        acc.push({ ativo_id: el.ativo_id });
      }
      return acc;
    }, []);
  }

  const VALIDATION_SCHEMA = Yup.object().shape({
    elementos: Yup.array().min(2, 'Selecione pelo menos dois ativos ou indicadores'),
    data: Yup.date().required(VALIDATIONS.REQUIRED).typeError(`${VALIDATIONS.INVALID_DATE}`),
    dias: Yup.number().required(VALIDATIONS.REQUIRED)
  });

  const xInitialValues = {
    elementos: [],
    data: dayjs(),
    dias: 252,
    incluir_variacoes: true
  };

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: (pValues, pActions) => {
      const { elementos, ...restValues } = pValues;
      const xItems = separarElementosPorOrigem(elementos);

      const xPayload = {
        ...restValues,
        data: dayjs(pValues.data).format('YYYY-MM-DD'),
        itens: xItems,
        corridos: false
      };

      services.consultas.correlacao(
        { data: xPayload },
        rRes => {
          const xResult = rRes.data;
          console.log('Resultado da correlação:', xResult);
          setData(xResult);
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    }
  });

  const handleDateChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const isUpMd = useMediaQuery(theme => theme.breakpoints.up('md'));

  const INDICADORES_TIPO = useRef({
    M: {
      label: 'Moeda',
      id: 'M',
      icon: 'coin'
    },
    C: {
      label: 'Correção Monetária',
      id: 'C',
      icon: 'coins'
    },
    I: {
      label: 'Índice de Mercado',
      id: 'I',
      icon: 'chart_line'
    },
    J: {
      label: 'Juros',
      id: 'J',
      icon: 'percent'
    },
    T: { label: 'Metal', id: 'T', icon: 'metal' }
  });

  const xIndicadoresTipo = INDICADORES_TIPO.current;

  const renderPessoa = pData => {
    const { pessoa_fantasia, pessoa, pessoa_tipo_id, cnpj, top_setor, setor, b3 } = pData;
    const xPessoaTipoList = utils.helpers.getPessoaTipoById(pessoa_tipo_id);

    return (
      <>
        <Stack flexGrow={1}>
          <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
            {pessoa_fantasia || pessoa}
          </Typography>
          <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                display: { xs: 'none', sm: 'inline-block' }
              }}>
              {pessoa}
            </Typography>
            {cnpj && (
              <Typography variant="caption" color="textSecondary">
                {`${displays.cnpj(cnpj)}`}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" sx={{ gap: '8px 8px' }} flexWrap="wrap" spacing={{ xs: 0, sm: 1 }}>
            {top_setor && !isUpMd && (
              <Chip
                label={top_setor}
                size="small"
                color="primary"
                sx={{ display: { xs: 'flex', md: 'none' } }}
              />
            )}
            {!validators.isEmpty(setor) && (
              <Chip
                key={setor}
                label={setor}
                size="small"
                color="secondary"
                sx={{
                  maxWidth: {
                    xs: '200px',
                    sm: '400px',
                    lg: '100%'
                  }
                }}
              />
            )}
            {xPessoaTipoList.map(xPessoaTipo => {
              return (
                <Chip
                  key={xPessoaTipo.descricao}
                  label={xPessoaTipo.descricao}
                  size="small"
                  color="secondary"
                  sx={{ display: { xs: 'none', md: 'flex' } }}
                />
              );
            })}
            {b3?.codigo_emissor && (
              <Chip
                label={b3?.codigo_emissor}
                size="small"
                color="secondary"
                sx={{ display: { xs: 'none', md: 'flex' } }}
              />
            )}
          </Stack>
        </Stack>
        {top_setor && isUpMd && (
          <Stack>
            <Stack justifyContent="end">
              <Chip label={top_setor} size="small" color="primary" />
            </Stack>
          </Stack>
        )}
      </>
    );
  };

  const renderAtivo = pData => {
    const {
      ticker,
      ativo_seuid,
      ativo,
      ativo_tipo,
      isin,
      mercado,
      emissor_cnpj,
      vencimento,
      serie
    } = pData;

    return (
      <>
        <Stack flexGrow={1}>
          <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
            {ticker || ativo_seuid || ativo}
          </Typography>
          <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                display: { xs: 'none', sm: 'inline-block' }
              }}>
              {ativo || isin}
            </Typography>
          </Stack>

          <Stack direction="row" sx={{ gap: '8px 8px' }} flexWrap="wrap" gap={{ xs: 0, sm: 1 }}>
            {mercado && (
              <Chip
                label={ATIVO_MERCADO[mercado].descricao}
                size="small"
                color="primary"
                sx={{ display: { xs: 'flex', md: 'none' } }}
              />
            )}

            {emissor_cnpj && (
              <Chip label={displays.cnpj(emissor_cnpj)} size="small" color="secondary" />
            )}

            {isin && <Chip label={isin} size="small" color="secondary" />}
            {vencimento && (
              <Chip
                label={`Venc.: ${formats.formatDate(vencimento)}`}
                size="small"
                color="secondary"
              />
            )}
            {serie && <Chip label={`Série: ${serie}`} size="small" color="secondary" />}
          </Stack>
        </Stack>
        {ativo_tipo && isUpMd && (
          <Stack>
            <Stack justifyContent="end">
              <Chip label={ativo_tipo} size="small" color="primary" />
            </Stack>
          </Stack>
        )}
      </>
    );
  };

  const renderIndicador = pData => {
    const { indicador_id, indicador, indicador_tipo_id } = pData;

    return (
      <>
        <Stack flexGrow={1}>
          <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
            {indicador_id}
          </Typography>
          <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                display: { xs: 'none', sm: 'inline-block' }
              }}>
              {indicador}
            </Typography>
          </Stack>

          <Stack direction="row" sx={{ gap: '8px 8px' }} flexWrap="wrap" spacing={{ xs: 0, sm: 1 }}>
            <Chip
              label={xIndicadoresTipo[indicador_tipo_id].label}
              size="small"
              color="primary"
              sx={{ display: { xs: 'flex', md: 'none' } }}
            />
          </Stack>
        </Stack>
        {isUpMd && (
          <Stack>
            <Stack justifyContent="end">
              <Chip
                label={xIndicadoresTipo[indicador_tipo_id].label}
                size="small"
                color="primary"
              />
            </Stack>
          </Stack>
        )}
      </>
    );
  };

  const renderOption = {
    pessoa: renderPessoa,
    ativo: renderAtivo,
    indicador: renderIndicador
  };

  return (
    <BaseTemplate title="Correlação" maxWidth="xl">
      <Stack gap={2}>
        <Stack gap={2}>
          <Card component="form" noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <Stack px={2} pb={2} pt={3}>
              <Stack gap={2}>
                <Stack flexGrow={1}>
                  <Autocomplete
                    multiple
                    id="pesquisa-elementos"
                    options={pesquisaOptions}
                    value={formik.values.elementos || []}
                    onChange={(_, newValue) => {
                      formik.setFieldValue('elementos', newValue, true);
                    }}
                    filterSelectedOptions
                    freeSolo={false}
                    filterOptions={options => options}
                    noOptionsText={
                      pesquisaLoading
                        ? 'Carregando...'
                        : pesquisaInput.length < 2
                        ? 'Digite pelo menos 2 caracteres'
                        : 'Nenhum resultado encontrado'
                    }
                    getOptionLabel={option => {
                      const label =
                        option.ticker ||
                        option.ativo_seuid ||
                        option.indicador_seuid ||
                        option.pessoa_fantasia ||
                        'Opção sem nome';
                      return label;
                    }}
                    isOptionEqualToValue={(option, value) => {
                      const isEqual = option.pesquisa_id === value.pesquisa_id;
                      return isEqual;
                    }}
                    loading={pesquisaLoading}
                    onInputChange={(_, value) => {
                      setPesquisaInput(value);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Buscar Ativos ou Indicadores"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {pesquisaLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <Stack
                          {...props}
                          key={option.pesquisa_id}
                          direction="row"
                          flexGrow={1}
                          spacing={3}
                          alignItems="flex-start"
                          component="li"
                          sx={{
                            px: 2,
                            py: 2,
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'gray',
                              backgroundColor: 'background.main'
                            },
                            borderBottom: '1px solid',
                            borderBottomColor: 'background.main'
                          }}>
                          <Stack>
                            {option.pesquisa_origem === 'indicador' ? (
                              <Stack
                                backgroundColor="primary.main"
                                width="36px"
                                height="36px"
                                justifyContent="center"
                                alignItems="center"
                                borderRadius="8px">
                                <Icon
                                  iconName={xIndicadoresTipo[option.indicador_tipo_id].icon}
                                  color="secondary"
                                  size={18}
                                />
                              </Stack>
                            ) : (
                              <PessoaImage
                                logoPessoaId={option.logo_pessoa_id}
                                alt={option.pessoa || option.ativo || option.indicador}
                                hasLogo={option.logo_ok}
                                size={36}
                              />
                            )}
                          </Stack>

                          {renderOption[option.pesquisa_origem](option)}
                        </Stack>
                      );
                    }}
                  />
                </Stack>
                <Stack direction="row" gap={2} flexGrow={1}>
                  <Stack flex={1}>
                    <DatePicker
                      label="Data"
                      name="data"
                      onChange={pValue => {
                        handleDateChange('data', pValue);
                      }}
                      value={formik.values.data}
                      disabled={formik.isSubmitting}
                      fullWidth
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          error: formik.errors.data,
                          helperText: formik.errors.data
                        }
                      }}
                    />
                  </Stack>
                  <Stack flex={1}>
                    <TextField
                      id="dias"
                      label="Dias"
                      name="dias"
                      type="number"
                      value={formik.values.dias}
                      variant="outlined"
                      onChange={e => {
                        // Permite apenas números inteiros positivos
                        const value = e.target.value;
                        if (value === '' || /^[0-9]+$/.test(value)) {
                          formik.setFieldValue('dias', value === '' ? '' : parseInt(value, 10));
                        }
                      }}
                      error={Boolean(formik.errors.dias)}
                      helperText={formik.errors.dias}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: 1,
                        step: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        formNoValidate: 'formnovalidate',
                        maxLength: 9
                      }}
                    />
                  </Stack>
                </Stack>

                <Stack flexGrow={1} direction="row" justifyContent={'flex-end'}>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.isValid}>
                    Calcular
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Card>
          <HeatMap correlacoes={data.correlacoes} itens={data.itens} />
          <VariacaoChart matriz={data.matriz} itens={data.itens} dias={data.dias} />
          {data.matriz && <CorrelacaoMatriz matriz={data.matriz} itens={data.itens} />}
          <CorrelacaoList itens={data.itens} />
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

export default withResponseHandling(CorrelacaoPares);
